import React, { Fragment, useRef, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import deepEqual from 'deep-equal';
import { useParams, useLocation } from 'react-router-dom';
import styles from './styles.scss';

import useRxStore from '../../../utils/use-rx-store';
import useRxDispatch from '../../../utils/use-rx-dispatch';
import {
  convertMetersToInchesForDisplay,
  convertMetersToFeetAndInchesForDisplay,
} from '../../../utils/measurement';

import { ResourceStatus } from '../../../types/resource';
import { Choice } from '../../../types/choice';
import {
  Sensor,
  SensorType,
  Capture,
  MultiPointInstallation,
  CAPTURE_STATUS_TO_PRETTY_NAME,
  ASSIGNABLE_TEAM_TO_PRETTY_NAME,
  SensorConfigChangeState,
} from '../../../types/api';
import {
  SensorsActionTypes,
  SensorsState,
  SensorTabs,
} from '../../../types/sensors';
import { DispatchType } from '../../../types/rx-actions';
import SensorsStore from '../../../rx-stores/sensors';
import SessionStore from '../../../rx-stores/session';
import { PERMISSIONS, hasPermission } from '../../../utils/permissions';

import {
  AppBar,
  AppBarContext,
  AppBarSection,
  AppBarTitle,
  Button,
  Checkbox,
  DayOfWeekPicker,
  Dialogger,
  getNextSortDirection,
  getDefaultSortFunction,
  ListView,
  ListViewColumn,
  Skeleton,
  Icons,
  Toaster,
} from '@densityco/ui';
import Modal from '../../Modal';
import HorizontalForm from '../../HorizontalForm';
import AppBarTabs from '../../AppBarTabs';
import colorVariables from '@densityco/ui/variables/colors.json';
import DataGrid, { FORCE_BREAK } from '../../DataGrid';
import ColoredPercent from '../../ColoredPercent';
import ColoredCaptureStatus from '../../ColoredCaptureStatus';
import ColoredOSVersion from '../../ColoredOSVersion';
import Loading from '../../Loading';
import DataCaptureStatusTag from '../../DataCaptureStatusTag';
import AddUpdateNoteForm from '../../AddUpdateNoteForm';
import ActionBar, {
  ActionBarCount,
  FilterOperatorInputChoices,
  FilterOperatorInputMultiChoices,
  FilterOperatorInputPercent,
} from '../../ActionBar';
import StatusTag from '../../StatusTag';
import { SortDirection } from '../../../types/sort';
import { FilterOperator } from '../../../types/filters';
import BackButton from '../../BackButton';
import CaptureTagViewer from '../../CaptureTagViewer';
import CaptureViewer from '../../CaptureViewer';
import SensorActionButtons from '../ActionButtons';
import SensorReannotateButton from '../../SensorReannotateButton';
import StopDataCaptureButton from '../ActionButtons/StopDataCapture';
import MeasureHeight from '../ActionButtons/MeasureHeight';
import CaptureScheduleModal from '../../CaptureScheduleModal';
import Note, {
  NoteConfigAttachment,
  NoteTextAttachment,
  NoteDataGridAttachment,
  NOTE_TYPES,
} from '../../Note';

import ErrorState from '../../ErrorState';
import ListViewTwoRowHeader from '../../ListViewTwoRowHeader';
import SetupStepInfo from '../../SetupStepInfo';

import { Hosts } from '../../../utils/host';
import { ON_PREM } from '../../../utils/environment';
import { fps30 } from '../../../utils/constants';
import { formatISOTimestamp } from '../../../utils/time';

function formatDuration(durationInSeconds: number): string {
  if (durationInSeconds < 1) {
    return `Under 1 second`;
  } else if (durationInSeconds < 60) {
    return `${durationInSeconds} second${durationInSeconds !== 1 ? 's' : ''}`;
  } else if (durationInSeconds < 3600) {
    const minutes = Math.round(durationInSeconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else {
    const hours = Math.round(durationInSeconds / 60 / 60);
    return `${hours} hour${hours !== 1 ? 's' : ''}`;
  }
}

const RichMPIName: React.FunctionComponent<{
  id: MultiPointInstallation['id'];
  mpi: MultiPointInstallation | null;
}> = ({ id, mpi }) => {
  const { organizationIdOrAll } = useParams();

  if (mpi) {
    return (
      <a
        className={styles.mpiNameTag}
        href={`#/${organizationIdOrAll}/multi-point-installations/${id}/edit`}
      >
        {mpi.name}
      </a>
    );
  } else {
    return <span>deleted MPI ({id})</span>;
  }
};

const SensorDetail: React.FunctionComponent<{}> = () => {
  const sensorsState = useRxStore(SensorsStore);
  const dispatch = useRxDispatch();
  const { organizationIdOrAll, sensorSerialNumber } = useParams();
  const routerLocation = useLocation();

  const bottomDetailCardWrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    dispatch({
      type: SensorsActionTypes.SENSORS_MOUNT_DETAIL,
      serialNumber: sensorSerialNumber,
      initialSelectedTab,
    });

    return () => {
      dispatch({
        type: SensorsActionTypes.SENSORS_UNMOUNT_DETAIL,
      });
    };
  }, [sensorSerialNumber]);

  // Set title to a semantic value to populate browser history
  useEffect(() => {
    if (sensorsState.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }
    const display =
      sensorsState.resource.data.sensor.doorway_name ||
      sensorsState.resource.data.sensor.serial_number;
    document.title = `Sensor ${display} - Density Sensei`;
    return () => {
      document.title = 'Density Sensei';
    };
  }, [
    sensorsState.resource.status === ResourceStatus.COMPLETE
      ? sensorsState.resource.data.sensor
      : null,
  ]);

  // Optionally, if a "initial-tab" query parameter is specified, scoll down to that tab's section
  // on the page.
  const params = new URLSearchParams(routerLocation.search.replace(/^\?/, ''));
  const initialSelectedTab = SensorTabs[params.get('initial-tab')] || null;
  useEffect(() => {
    if (!initialSelectedTab) {
      return;
    }
    if (!bottomDetailCardWrapperRef.current) {
      return;
    }
    if (sensorsState.resource.status !== ResourceStatus.COMPLETE) {
      return;
    }
    bottomDetailCardWrapperRef.current.scrollIntoView();
  }, [
    initialSelectedTab,
    sensorsState.resource.status,
    sensorsState.notes.resource.status,
  ]);

  switch (sensorsState.resource.status) {
    case ResourceStatus.IDLE:
    case ResourceStatus.LOADING:
      return (
        <div className={styles.wrapper}>
          <AppBar>
            <AppBarTitle>
              <BackButton />
              <Skeleton width={200} height={24} />
            </AppBarTitle>
          </AppBar>
          <Loading />
        </div>
      );

    case ResourceStatus.ERROR:
      return (
        <div className={styles.wrapper}>
          <AppBar>
            <AppBarTitle>
              <BackButton />
            </AppBarTitle>
          </AppBar>
          <ErrorState error={sensorsState.resource.error} />
        </div>
      );

    case ResourceStatus.COMPLETE:
      const selectedSensor =
        sensorsState.resource.data.sensor;
      if (!selectedSensor) {
        return null;
      }

      const busiestCapture =
        sensorsState.captures.resource.status ===
          ResourceStatus.COMPLETE &&
        sensorsState.captures.resource.data.length > 0
          ? sensorsState.captures.resource.data[0]
          : null;

      return (
        <Fragment>
          {/* Note deletion confirm box */}
          <Modal
            visible={sensorsState.notes.currentlyDeletingNoteId !== null}
            width={375}
            height={256}
            onBlur={() =>
              dispatch({
                type:
                  SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_DISMISS,
              })
            }
            onEscape={() =>
              dispatch({
                type:
                  SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_DISMISS,
              })
            }
          >
            <div
              className={styles.deleteNoteModalWrapper}
              data-test-id="delete-note-confirm-box"
            >
              <AppBar>
                <AppBarTitle>Delete Note</AppBarTitle>
              </AppBar>
              <div className={styles.deleteNoteModalBody}>
                Are you sure you'd like to delete this note? Once deleted, notes
                cannot be recovered.
              </div>
              <AppBarContext.Provider value="BOTTOM_ACTIONS">
                <AppBar>
                  <AppBarSection />
                  <AppBarSection>
                    <HorizontalForm>
                      <Button
                        variant="underline"
                        onClick={() =>
                          dispatch({
                            type:
                              SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_DISMISS,
                          })
                        }
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="filled"
                        onClick={() =>
                          dispatch({
                            type:
                              SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE_CONFIRM,
                            noteId:
                              sensorsState.notes.currentlyDeletingNoteId,
                          })
                        }
                        data-test-id="delete-note-submit"
                      >
                        Delete Note
                      </Button>
                    </HorizontalForm>
                  </AppBarSection>
                </AppBar>
              </AppBarContext.Provider>
            </div>
          </Modal>

          <AppBar>
            <AppBarTitle>
              <BackButton />
              <div className={styles.pageTitle}>
                {selectedSensor.doorway_name || `Sensor ${selectedSensor.serial_number}`}
              </div>
            </AppBarTitle>
            <AppBarSection>
              <SensorActionButtons
                sensors={[selectedSensor]}
                presentation="detail"
                showMeasureHeightButton={selectedSensor.sensor_type !== SensorType.OB1}
                primaryActionButton={
                  <Button
                    variant="filled"
                    href={`#/${organizationIdOrAll}/sensors/${selectedSensor.serial_number}/configure`}
                    leftIcon={<Icons.Lab />}
                  >
                    Configure
                  </Button>
                }
                // If a change is made that requires refetching sensor info, refetch it from the
                // server after the action is performed
                onReloadSensors={serialNumbers => {
                  if (!serialNumbers.includes(selectedSensor.serial_number)) {
                    return;
                  }

                  dispatch({
                    type: SensorsActionTypes.SENSORS_RELOAD_DETAIL,
                    serialNumber: sensorSerialNumber,
                  });
                }}
              />
            </AppBarSection>
          </AppBar>

          <div className={styles.scroll}>
            {selectedSensor.doorway === null ? (
              <div className={styles.doorwayNotLinkedWarning}>
                <h3>No doorway is linked to this installation</h3>
                <p>
                  <strong>
                    Installations and Doorways are two related concepts that
                    need to be linked in order to enable some functionality on
                    this page.
                  </strong>{' '}
                  Here's how to do this:
                </p>
                <ul>
                  <li>
                    Visit{' '}
                    <a href={`${Hosts.CoreCucumberHost}/api/doorway/`}>
                      Core Cucumber
                    </a>{' '}
                    and click doorways
                  </li>
                  <li>
                    Find the doorway that this installation should match up with
                  </li>
                  <li>
                    In the field labeled "installation id", enter{' '}
                    <code>{sensorSerialNumber}</code>, and save the form
                  </li>
                  <li>
                    Refresh this page, and if everything worked, sensei should
                    link up the doorway and installation.
                  </li>
                </ul>
                <p>
                  The implementation team is aware of this issue and are working
                  on a solution to minimize the amount of time this happens.
                </p>
              </div>
            ) : null}

            {selectedSensor.latest_config_change_id && selectedSensor.latest_config_change_current_state !== SensorConfigChangeState.COMPLETE ? (
              <div className={styles.sensorConfigurationUpdateInfo}>
                <h3>Sensor Configuration Update - In Progress</h3>
                <p>
                  The system is syncing the latest sensor config (version{' '}
                  {selectedSensor.latest_config_change_new_config.version}) to the sensor.
                </p>
              </div>
            ) : null}

            {selectedSensor.capture_priority_ends_at ? (
              <div
                className={styles.priorityAnnotationsWarning}
                data-test-id="priority-annotations-enabled"
              >
                <h3>Priority Annotations Enabled</h3>
                <p>
                  Any captures this sensor uploads until{' '}
                  <strong>{formatISOTimestamp(selectedSensor.capture_priority_ends_at)}</strong> will be prioritized by
                  the annotation provider.
                </p>
              </div>
            ) : null}

            {!ON_PREM ? (
              <div className={styles.topSection}>
                <div className={styles.captureViewer}>
                  {/* TODO: display the busiest capture, and a message here if no captures exist for this sensor */}
                  {sensorsState.captures.resource.status ===
                    ResourceStatus.COMPLETE &&
                  sensorsState.captures.resource.data.length > 0 ? (
                    <CaptureViewer
                      readOnly
                      autoPlay
                      width={500}
                      capture={busiestCapture}
                      configState={{
                        mutable_config: selectedSensor.assigned_config,
                        mutable_config_template:
                          selectedSensor.assigned_config_template,
                        config_status: selectedSensor.config_status,
                      }}
                    />
                  ) : null}
                </div>

                <div className={styles.latestReportMetrics}>
                  {selectedSensor.latest_report ? (
                    <h3>
                      Latest Report:{' '}
                      {selectedSensor.latest_report.report_name}
                    </h3>
                  ) : (
                    <h3>No Reports Run</h3>
                  )}
                  <DataGrid
                    items={[
                      {
                        id: 'egress accuracy',
                        label: 'Egress Accuracy',
                        value: (
                          <ColoredPercent
                            percent={
                              selectedSensor.latest_report ? selectedSensor.latest_report.egress_accuracy : null
                            }
                          />
                        ),
                      },
                      {
                        id: 'annotated egresses',
                        label: 'Annotated Egresses',
                        tooltip:
                          'Egress events that are marked by an annotator; what we consider to be Ground Truth.',
                        value: selectedSensor.latest_report ? (
                          selectedSensor.latest_report.egresses_annotated
                        ) : (
                          <Fragment>&mdash;</Fragment>
                        ),
                      },
                      {
                        id: 'extra egresses',
                        label: 'Extra Egresses',
                        tooltip:
                          'Events where the algorithm marked an egress which does not have a corresponding egress annotation.',
                        value: selectedSensor.latest_report ? (
                          selectedSensor.latest_report.egresses_extra
                        ) : (
                          <Fragment>&mdash;</Fragment>
                        ),
                      },
                      {
                        id: 'missed egresses',
                        label: 'Missed Egresses',
                        tooltip:
                          'Events where the algorithm did not register an egress, but where in actuality there is an egress annotation.',
                        value: selectedSensor.latest_report ? (
                          selectedSensor.latest_report.egresses_missed
                        ) : (
                          <Fragment>&mdash;</Fragment>
                        ),
                      },
                      FORCE_BREAK,
                      {
                        id: 'ingress accuracy',
                        label: 'Ingress Accuracy',
                        value: (
                          <ColoredPercent
                            percent={
                              selectedSensor.latest_report ? selectedSensor.latest_report.ingress_accuracy : null
                            }
                          />
                        ),
                      },
                      {
                        id: 'annotated ingresses',
                        label: 'Annotated Ingresses',
                        tooltip:
                          'Ingresses events that are marked by an annotator; what we consider to be ground truth.',
                        value: selectedSensor.latest_report ? (
                          selectedSensor.latest_report.ingresses_annotated
                        ) : (
                          <Fragment>&mdash;</Fragment>
                        ),
                      },
                      {
                        id: 'extra ingresses',
                        label: 'Extra Ingresses',
                        tooltip:
                          'Events where the algorithm registered an ingress which does not have a corresponding ingress annotation.',
                        value: selectedSensor.latest_report ? (
                          selectedSensor.latest_report.ingresses_extra
                        ) : (
                          <Fragment>&mdash;</Fragment>
                        ),
                      },
                      {
                        id: 'missed ingresses',
                        label: 'Missed Ingresses',
                        tooltip:
                          'Events where the algorithm did not register an ingress, but where in actuality there is an ingress annotation.',
                        value: selectedSensor.latest_report ? (
                          selectedSensor.latest_report.ingresses_missed
                        ) : (
                          <Fragment>&mdash;</Fragment>
                        ),
                      },
                      FORCE_BREAK,
                      {
                        id: 'overall accuracy',
                        label: 'Overall Accuracy',
                        value: (
                          <ColoredPercent
                            percent={
                              selectedSensor.latest_report ?  selectedSensor.latest_report.total_accuracy : null
                            }
                          />
                        ),
                      },
                    ]}
                  />
                </div>
              </div>
            ) : null}

            <div className={styles.detailCard}>
              <AppBar>
                <AppBarTitle>Sensor</AppBarTitle>
              </AppBar>
              <div className={styles.detailCardDataGrid}>
                <DataGrid
                  items={[
                    {
                      id: 'serialnumber',
                      label: 'Serial Number',
                      value: selectedSensor.serial_number,
                    },
                    {
                      id: 'status',
                      label: 'Status',
                      value: (
                        <StatusTag status={selectedSensor.current_status} />
                      ),
                    },
                    {
                      id: 'lastheartbeat',
                      label: 'Last Heartbeat',
                      value: selectedSensor.last_heartbeat ? (
                        formatISOTimestamp(selectedSensor.last_heartbeat)
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    {
                      id: 'lastcount',
                      label: 'Last Counted At',
                      value: selectedSensor.last_counted ? (
                        formatISOTimestamp(selectedSensor.last_counted)
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    FORCE_BREAK,
                    {
                      id: 'setupstep',
                      label: 'Setup Step',
                      value: <SetupStepInfo
                        currentState={selectedSensor.current_state}
                        currentStateMetadata={selectedSensor.current_state_metadata}
                      />,
                    },
                    {
                      id: 'capturestatus',
                      label: 'Capture Status',
                      value: (
                        <DataCaptureStatusTag sensor={selectedSensor} />
                      ),
                    },
                    {
                      id: 'osversion',
                      label: 'OS Version',
                      value: selectedSensor.os_version ? (
                        <ColoredOSVersion osVersion={selectedSensor.os_version} />
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    FORCE_BREAK,
                    {
                      id: 'currentstate',
                      label: 'Current State',
                      value: selectedSensor.current_state || (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    {
                      id: 'assignedteam',
                      label: 'Assigned Team',
                      value: selectedSensor.assigned_team ? (
                        ASSIGNABLE_TEAM_TO_PRETTY_NAME[selectedSensor.assigned_team]
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                  ]}
                />
              </div>
            </div>

            {/* Captures, notes, etc */}
            <BottomDetailCard
              sensorsState={sensorsState}
              selectedSensor={selectedSensor}
              busiestCapture={busiestCapture}
              wrapperRef={bottomDetailCardWrapperRef}
            />
          </div>
        </Fragment>
      );
  }
};
export default SensorDetail;

const BottomDetailCard: React.FunctionComponent<{
  sensorsState: SensorsState;
  selectedSensor: Sensor;
  busiestCapture: Capture;
  wrapperRef: React.Ref<HTMLDivElement>;
}> = ({
  sensorsState,
  selectedSensor,
  busiestCapture,
  wrapperRef,
}) => {
  const sessionState = useRxStore(SessionStore);
  const dispatch = useRxDispatch();
  let tabs = [
    {
      id: SensorTabs.CAPTURES,
      label: 'Captures',
      onClick: () =>
        dispatch({
          type: SensorsActionTypes.SENSORS_SWITCH_TAB,
          tab: SensorTabs.CAPTURES,
        }),
    },
    {
      id: SensorTabs.NOTES,
      label: 'Timeline & Notes',
      onClick: () =>
        dispatch({
          type: SensorsActionTypes.SENSORS_SWITCH_TAB,
          tab: SensorTabs.NOTES,
        }),
    },
    {
      id: SensorTabs.DIAGNOSTIC_INFO,
      label: 'Diagnostic Info',
      onClick: () =>
        dispatch({
          type: SensorsActionTypes.SENSORS_SWITCH_TAB,
          tab: SensorTabs.DIAGNOSTIC_INFO,
        }),
    },
  ];

  // Don't show "captures" when running in on prem mode.
  if (ON_PREM) {
    tabs = tabs.filter(t => t.id !== SensorTabs.CAPTURES);
  }

  const renderCaptureLifecycleStep = (
    status: keyof Sensor['captures']['by_status'],
  ) => {
    const percent =
      selectedSensor.captures.total === 0
        ? 0
        : (selectedSensor.captures.by_status[status] /
            selectedSensor.captures.total) *
          100;
    return (
      <Fragment>
        {selectedSensor.captures.by_status[status]}&nbsp;
        <span className={styles.captureLifecyclePercent}>
          ({Math.round(percent)}%)
        </span>
      </Fragment>
    );
  };

  // Calculate a list of tags to show when picking a tag in the capture filter
  const tagChoicesForCaptureFilter =
    sensorsState.captures.resource.status === ResourceStatus.COMPLETE
      ? sensorsState.captures.resource.data
          .flatMap(capture => capture.tags)
          .filter(tag => !tag.name.includes('F8'))
          .map(tag => ({ id: tag.id, label: tag.name }))
          .reduce(
            // Deduplicates the list of tags
            (acc, choice) =>
              !acc.find(i => i.id === choice.id) ? [...acc, choice] : acc,
            [] as Array<{ id: any; label: any }>,
          )
      : [];

      const deleteCaptures = async (captureIds: Array<Capture["id"]>) => {
        const ok = await Dialogger.confirm({
          prompt: (
            <Fragment>
              Are you sure you would like to delete{' '}
              {captureIds.length === 1 ? (
                <Fragment>
                  capture <strong>{captureIds[0]}</strong>
                </Fragment>
              ): (
                <Fragment>
                  <strong>{captureIds.length}</strong> captures
                </Fragment>
              )}?
            </Fragment>
          ),
        });

        if (!ok) {
          return;
        }

        dispatch({
          type: SensorsActionTypes.SENSORS_CAPTURES_DELETE,
          captureIds,
        });
      }

    const capturesActionBar = (
      <div className={styles.detailCardActionBar}>
        <ActionBar
          units="Captures"
          totalItemCount={
            sensorsState.captures.resource.status === ResourceStatus.COMPLETE
              ? sensorsState.captures.resource.data.length
              : null
          }
          selectedItemCount={sensorsState.captures.selectedCaptureIds.length}
          onClearSelectedItems={() =>
            dispatch({
              type: SensorsActionTypes.SENSORS_CAPTURES_CHANGE_CHECKS,
              checked: false,
              ids: sensorsState.captures.selectedCaptureIds,
            })
          }
          selectedActionButtons={
            <HorizontalForm>
              <SensorReannotateButton captureIds = { sensorsState.captures.selectedCaptureIds }/>
              <Button
                leftIcon={<Icons.Trash />}
                onClick={() => {
                  deleteCaptures(
                    sensorsState.captures.selectedCaptureIds
                  )
                }}
              >Delete Selected Captures</Button>
            </HorizontalForm>
          }
          actionButtons={
            <HorizontalForm>
              <Button
                onClick={() => {
                  if (sensorsState.captures.resource.status !== ResourceStatus.COMPLETE) {
                    return;
                  }
                  deleteCaptures(
                    sensorsState.captures.resource.data
                      .filter(i => i.low_integration_video_url === null)
                      .map(i => i.id)
                  );
                }}
              >
                Delete all non-HDR captures
              </Button>
            </HorizontalForm>
          }
          filterDefinitions={[
            {
              id: 'status',
              label: 'Status',
              operators: [
                {
                  id: 'equals',
                  label: 'equals',
                  allowMultiple: false,
                  initialValue: null,
                  isValueValid: sensorsState =>
                    sensorsState != null,
                  component: props => (
                    <FilterOperatorInputChoices
                      {...props}
                      choices={Object.entries(
                        CAPTURE_STATUS_TO_PRETTY_NAME,
                      ).map(([k, v]) => ({
                        id: k,
                        label: (
                          <div>
                            <div
                              style={{
                                fontSize: 14,
                                marginBottom: 4,
                              }}
                            >
                              {v[0]}
                            </div>
                            <div style={{ fontSize: 10 }}>
                              {v[1]}
                            </div>
                          </div>
                        ),
                      }))}
                    />
                  ),
                } as FilterOperator<Choice['id'] | null>,
              ],
            },
            {
              id: 'tags',
              label: 'Tags',
              operators: [
                {
                  id: 'in',
                  label: 'is one of',
                  allowMultiple: false,
                  initialValue: [],
                  component: props => (
                    <FilterOperatorInputMultiChoices
                      {...props}
                      choices={tagChoicesForCaptureFilter}
                    />
                  ),
                  isValueValid: value =>
                    value.length > 0 &&
                    value.map(i =>
                      tagChoicesForCaptureFilter
                        .map(j => j.id)
                        .includes(i),
                    ),
                } as FilterOperator<Array<Choice['id']>>,
              ],
            },
            {
              id: 'total_accuracy',
              label: 'Total Accuracy',
              operators: [
                {
                  id: 'lt',
                  label: 'Less Than',
                  allowMultiple: false,
                  isValueValid: s => !isNaN(parseFloat(s)),
                  initialValue: '',
                  component: FilterOperatorInputPercent,
                } as FilterOperator<string>,
                {
                  id: 'gt',
                  label: 'Greater Than',
                  allowMultiple: false,
                  isValueValid: s => !isNaN(parseFloat(s)),
                  initialValue: '',
                  component: FilterOperatorInputPercent,
                } as FilterOperator<string>,
              ],
            },
          ]}
          filters={sensorsState.captures.filters}
          onChangeFilters={filters =>
            dispatch({
              type:
                SensorsActionTypes.SENSORS_CAPTURES_SET_FILTERS,
              filters,
            })
          }
        />
      </div>
    );

  return (
    <Fragment>
      <div className={styles.detailCard} ref={wrapperRef} data-test-id="bottom-card-wrapper">
        <AppBar>
          {tabs.length > 1 ? (
            <AppBarTabs tabs={tabs} activeTab={sensorsState.activeTab} />
          ) : (
            <AppBarTitle>{tabs[0].label}</AppBarTitle>
          )}
        </AppBar>
        {sensorsState.activeTab === SensorTabs.CAPTURES ? (
          <Fragment>
            <div className={styles.detailCardDataGrid}>
              <DataGrid
                items={[
                  {
                    id: 'total',
                    label: 'Total',
                    value: selectedSensor.captures.total,
                  },
                  FORCE_BREAK,
                  {
                    id: 'failed',
                    label: 'failed',
                    value: renderCaptureLifecycleStep('failed'),
                  },
                  {
                    id: 'created',
                    label: 'Created',
                    value: renderCaptureLifecycleStep('created'),
                  },
                  {
                    id: 'frozen',
                    label: 'Frozen',
                    value: renderCaptureLifecycleStep('frozen'),
                  },
                  FORCE_BREAK,
                  {
                    id: 'preparing_to_send',
                    label: 'Preparing to send',
                    value: renderCaptureLifecycleStep('preparing_to_send'),
                  },
                  {
                    id: 'waiting_for_annotations',
                    label: 'Waiting for annotations',
                    value: renderCaptureLifecycleStep(
                      'waiting_for_annotations',
                    ),
                  },
                  {
                    id: 'mpi_pending_annotation',
                    label: 'MPI pending annotation',
                    value: renderCaptureLifecycleStep('mpi_pending_annotation'),
                  },
                  {
                    id: 'processing_annotations',
                    label: 'Processing annotations',
                    value: renderCaptureLifecycleStep('processing_annotations'),
                  },
                  {
                    id: 'annotated',
                    label: 'Annotated',
                    value: renderCaptureLifecycleStep('annotated'),
                  },
                  {
                    id: 'incorrectly_annotated',
                    label: 'Incorrectly Annotated',
                    value: renderCaptureLifecycleStep('incorrectly_annotated'),
                  },
                  FORCE_BREAK,
                  {
                    id: 'preparing_to_score',
                    label: 'Preparing to score',
                    value: renderCaptureLifecycleStep('preparing_to_score'),
                  },
                  {
                    id: 'scored',
                    label: 'Scored',
                    value: renderCaptureLifecycleStep('scored'),
                  },
                  FORCE_BREAK,
                  {
                    id: 'waiting_for_qa',
                    label: 'Waiting for QA',
                    value: renderCaptureLifecycleStep('waiting_for_qa'),
                  },
                  {
                    id: 'qa_complete',
                    label: 'QA Complete',
                    value: renderCaptureLifecycleStep('qa_complete'),
                  },
                  FORCE_BREAK,
                  {
                    id: 'preparing_to_post_qa_score',
                    label: 'Preparing to post QA score',
                    value: renderCaptureLifecycleStep(
                      'preparing_to_post_qa_score',
                    ),
                  },
                  {
                    id: 'post_qa_score_complete',
                    label: 'Post QA score complete',
                    value: renderCaptureLifecycleStep('post_qa_score_complete'),
                  },
                  {
                    id: 'mpi_scored',
                    label: 'MPI Scored',
                    value: renderCaptureLifecycleStep('mpi_scored'),
                  },
                ]}
              />
            </div>
            {[ResourceStatus.IDLE, ResourceStatus.LOADING].includes(
              sensorsState.captures.resource.status,
            ) ? (
              <Fragment>
                {capturesActionBar}
                <Loading />
              </Fragment>
            ) : null}
            {sensorsState.captures.resource.status ===
            ResourceStatus.COMPLETE ? (
              <Fragment>
                {capturesActionBar}
                <div className={styles.captureWrapper}>
                  <ListView
                    padOuterColumns
                    headerHeight={48}
                    rowHeight="auto"
                    data={sensorsState.captures.resource.data}
                    sort={[sensorsState.captures.sort]}
                    onClickHeader={(column: string, template: any) => {
                      // Don't allow filtering these columns
                      if (column === 'View' || column === 'Checkboxes') {
                        return;
                      }
                      if (
                        sensorsState.captures.resource.status !==
                        ResourceStatus.COMPLETE
                      ) {
                        return;
                      }

                      const lastSortDirection =
                        column === sensorsState.captures.sort.column
                          ? sensorsState.captures.sort.direction
                          : 'none';
                      const sortDirection: SortDirection = getNextSortDirection(
                        lastSortDirection,
                      );

                      dispatch({
                        type:
                          SensorsActionTypes.SENSORS_CAPTURES_SET_SORT,
                        column,
                        direction: sortDirection,
                        captures: sensorsState.captures.resource.data
                          .slice()
                          .sort(
                            getDefaultSortFunction(template, sortDirection),
                          ),
                      });
                    }}
                  >
                    <ListViewColumn
                      id="Checkboxes"
                      title={
                        <Checkbox
                          data-test-id="sensor-detail-captures-all-checkbox"
                          checked={sensorsState.captures.resource.status === ResourceStatus.COMPLETE ? (
                            sensorsState.captures.resource.data.length > 0 &&
                            sensorsState.captures.resource.data.length === sensorsState.captures.selectedCaptureIds.length
                          ) : false}
                          onChange={e => {
                            if (sensorsState.captures.resource.status !== ResourceStatus.COMPLETE) {
                              return;
                            }
                            dispatch({
                              type: SensorsActionTypes.SENSORS_CAPTURES_CHANGE_CHECKS,
                              checked: e.target.checked,
                              ids: sensorsState.captures.resource.data.map(i => i.id),
                            })
                          }}
                        />
                      }
                      width={64}
                      template={(capture: Capture) => (
                        <Checkbox
                          data-test-id="sensor-detail-captures-checkbox"
                          checked={sensorsState.captures.selectedCaptureIds.includes(capture.id)}
                          onChange={e =>
                            dispatch({
                              type: SensorsActionTypes.SENSORS_CAPTURES_CHANGE_CHECKS,
                              checked: e.target.checked,
                              ids: [capture.id],
                            })
                          }
                        />
                      )}
                    />
                    <ListViewColumn
                      id="ID"
                      width={100}
                      template={(capture: Capture) => (
                        <span
                          style={{
                            height: 56,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          {capture.id}
                        </span>
                      )}
                    />
                    <ListViewColumn
                      id="HDR"
                      width={75}
                      template={(capture: Capture) => capture.low_integration_video_url ? (
                        <span className={styles.hdrCaptureLabel}>Yes</span>
                      ) : (
                        <span className={styles.nonHdrCaptureLabel}>No</span>
                      )}
                      valueTemplate={(capture: Capture) => capture.low_integration_video_url ? 1 : 0}
                    />
                    <ListViewColumn
                      id="Video Starts At"
                      width={150}
                      template={(capture: Capture) => {
                        if (!capture.video_starts_at) {
                          return (
                            <Fragment>&mdash;</Fragment>
                          );
                        }
                        return (
                          <div>
                            {formatISOTimestamp(capture.video_starts_at, true)}
                          </div>
                        )
                      }}
                      valueTemplate={(capture: Capture) => capture.created_at}
                    />
                    <ListViewColumn
                      id="Annotation Status"
                      template={(capture: Capture) => (
                        <ColoredCaptureStatus captureStatus={capture.annotation_status} />
                      )}
                    />
                    <ListViewColumn
                      id="Tags"
                      template={(capture: Capture) => (
                        <CaptureTagViewer
                          tags={capture.tags.filter(
                            tag => !tag.name.includes('F8'),
                          )}
                          onNavigateToTag={tag => {
                            const annotationTool = `${Hosts.AnnotationToolHost}/captures/${
                              capture.id
                            }/${
                              capture.latest_report
                                ? `?report_name=${capture.latest_report.report_name}`
                                : ''
                            }`;
                            window.open(
                              `${annotationTool}&t=${tag.start_frame / fps30}`,
                              '_blank',
                            );
                          }}
                        />
                      )}
                      valueTemplate={(capture: Capture) => capture.tags.length}
                    />
                    <ListViewColumn
                      id="Ingress Accuracy"
                      title={
                        <ListViewTwoRowHeader
                          header="Ingress Accuracy"
                          subheader="From the latest report"
                        />
                      }
                      template={(capture: Capture) => (
                        <ColoredPercent
                          percent={capture.latest_report ? capture.latest_report.ingress_accuracy : null}
                        />
                      )}
                      valueTemplate={(capture: Capture) =>
                        capture.latest_report && capture.latest_report.ingress_accuracy !== null
                          ? capture.latest_report.ingress_accuracy * 100
                          : null
                      }
                    />
                    <ListViewColumn
                      id="Egress Accuracy"
                      title={
                        <ListViewTwoRowHeader
                          header="Egress Accuracy"
                          subheader="From the latest report"
                        />
                      }
                      template={(capture: Capture) => (
                        <ColoredPercent
                          percent={capture.latest_report ? capture.latest_report.egress_accuracy : null}
                        />
                      )}
                      valueTemplate={(capture: Capture) =>
                        capture.latest_report &&
                        capture.latest_report.egress_accuracy !== null
                          ? capture.latest_report.egress_accuracy * 100
                          : null
                      }
                    />
                    <ListViewColumn
                      id="Accuracy"
                      title={
                        <ListViewTwoRowHeader
                          header="Overall Accuracy"
                          subheader="From the latest report"
                        />
                      }
                      template={(capture: Capture) => (
                        <ColoredPercent
                          percent={capture.latest_report ? capture.latest_report.total_accuracy : null}
                        />
                      )}
                      valueTemplate={(capture: Capture) =>
                        capture.latest_report && capture.latest_report.total_accuracy !== null
                          ? capture.latest_report.total_accuracy * 100
                          : null
                      }
                    />
                    <ListViewColumn
                      id="View"
                      title=" "
                      width={252}
                      template={(capture: Capture) => (
                        <HorizontalForm>
                          <Button
                            target="_blank"
                            onClick={() => deleteCaptures([capture.id])}
                          >
                            Delete
                          </Button>

                          <Button
                            variant="filled"
                            target="_blank"
                            href={`${Hosts.AnnotationToolHost}/captures/${
                              capture.id
                            }/${
                              capture.latest_report
                                ? `?report_name=${capture.latest_report.report_name}`
                                : ''
                            }`}
                          >
                            View in Ann. Tool
                          </Button>
                        </HorizontalForm>
                      )}
                    />
                  </ListView>
                </div>
              </Fragment>
            ) : null}
          </Fragment>
        ) : null}
        {sensorsState.activeTab === SensorTabs.NOTES ? (
          <Fragment>
            {sensorsState.notes.resource.status === ResourceStatus.IDLE ||
            sensorsState.notes.resource.status ===
              ResourceStatus.LOADING ? (
              <AppBar>
                <AppBarTitle>
                  <Skeleton width={200} height={24} />
                </AppBarTitle>
              </AppBar>
            ) : null}
            {sensorsState.notes.resource.status ===
            ResourceStatus.ERROR ? (
              <AppBar></AppBar>
            ) : null}
            {sensorsState.notes.resource.status ===
            ResourceStatus.COMPLETE ? (
              <AppBar>
                <AppBarSection>
                  <div className={styles.noteCountWrapper}>
                    <ActionBarCount
                      units="User Actions"
                      total={
                        sensorsState.notes.resource.data.filter(
                          i => i.user,
                        ).length
                      }
                    />
                  </div>
                  <div className={styles.noteCountWrapper}>
                    <ActionBarCount
                      units="System Events"
                      total={
                        sensorsState.notes.resource.data.filter(
                          i => !i.user,
                        ).length
                      }
                    />
                  </div>
                </AppBarSection>
                <AppBarSection>
                  <HorizontalForm>
                    {selectedSensor.current_state === null && hasPermission(sessionState, PERMISSIONS.ENABLE_EVENT_COLLECTION_MACHINE) ? (
                      <Button
                        onClick={() =>
                          dispatch({
                            type: SensorsActionTypes.SENSORS_STATE_TRANSITION,
                            state: "SENSOR_ONLINE",
                          })
                        }
                      >
                        Begin Automatic Data Collection
                      </Button>
                    ) : null}
                    <Button
                      onClick={() => {
                        dispatch({
                          type:
                            SensorsActionTypes.SENSORS_NOTES_RELOAD
                        });
                      }}
                      leftIcon={<Icons.Refresh />}
                    >
                      Refresh Notes
                    </Button>
                    {!sensorsState.notes.creation.active ? (
                      <Button
                        variant="filled"
                        onClick={() => {
                          if (sensorsState.notes.creation.active) {
                            dispatch({
                              type:
                                SensorsActionTypes.SENSORS_NOTES_CREATION_CLOSE,
                            });
                          } else {
                            dispatch({
                              type:
                                SensorsActionTypes.SENSORS_NOTES_CREATION_OPEN,
                            });
                          }
                        }}
                        data-test-id="add-note-button"
                      >
                        Add Note
                      </Button>
                    ) : null}
                  </HorizontalForm>
                </AppBarSection>
              </AppBar>
            ) : null}
          </Fragment>
        ) : null}
        {sensorsState.activeTab === SensorTabs.DIAGNOSTIC_INFO ? (
          <Fragment>
            {sensorsState.resource.status === ResourceStatus.IDLE ||
            sensorsState.resource.status === ResourceStatus.LOADING ? (
              <AppBar>
                <AppBarTitle>
                  <Skeleton width={200} height={24} />
                </AppBarTitle>
              </AppBar>
            ) : null}
            {sensorsState.resource.status === ResourceStatus.ERROR ? (
              <AppBar></AppBar>
            ) : null}
            {sensorsState.resource.status === ResourceStatus.COMPLETE ? (
              <div className={styles.diagnosticInfoWrapper}>
                <pre className={styles.diagnosticInfoPre}>
                  {JSON.stringify(
                    sensorsState.resource.data.sensor
                      .diagnostic_info_json,
                    null,
                    2,
                  )}
                </pre>
              </div>
            ) : null}
          </Fragment>
        ) : null}
      </div>
      {sensorsState.activeTab === SensorTabs.NOTES ? (
        <NoteList
          notes={sensorsState.notes}
          busiestCapture={busiestCapture}
          selectedSensor={selectedSensor}
        />
      ) : null}
    </Fragment>
  );
};

const NoteList: React.FunctionComponent<{
  selectedSensor: Sensor,
  notes: SensorsState['notes'];
  busiestCapture: Capture;
}> = ({ selectedSensor, notes, busiestCapture }) => {
  const dispatch = useRxDispatch();
  const [expandedNoteGroupIds, setExpandedNoteGroupIds] = useState([]);

  const [captureScheduleModalOpen, setCaptureScheduleModalOpen] = useState(false);
  const [captureScheduleModalSensors, setCaptureScheduleModalSensors] = useState([]);

  const renderNote = (note) => {
    let editing = false,
      noteBody = note.data.body;
    if (notes.editing.active) {
      editing = notes.editing.id === note.id;

      if (editing) {
        noteBody = notes.editing.body;
      }
    }

    // Show the footer when a note is editable but a note on the page isn't currently
    // being edited.
    let footerDisabled = true;
    if (note.user_editable && !notes.editing.active) {
      footerDisabled = false;
    }
    if (!note.data.body) {
      footerDisabled = true;
    }

    const noteProps = {
      type: note.type,
      body: noteBody,
      timestamp: note.created_at,
      from: note.user,
      editing: editing,
      isCollapsedByDefault: note.is_collapsed_by_default,
      actions: note.actions,
      onClickAction: noteAction =>
        dispatch({
          type: SensorsActionTypes.SENSORS_NOTES_CLICK_NOTE_ACTION,
          noteId: note.id,
          noteAction,
        }),
      onEditNote: () =>
        dispatch({
          type: SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE,
          noteId: note.id,
        }),
      onDeleteNote: () =>
        dispatch({
          type: SensorsActionTypes.SENSORS_NOTES_DELETE_NOTE,
          noteId: note.id,
        }),
      onCancelEditNote: () =>
        dispatch({
          type: SensorsActionTypes.SENSORS_NOTES_CANCEL_EDIT_NOTE,
        }),
      onSubmitEditNote: () =>
        dispatch({
          type: SensorsActionTypes.SENSORS_NOTES_SUBMIT_EDIT_NOTE,
        }),
      submitDisabled:
        // Disable the note's controls if:
        // 1) it's not the actively edited one and there's anotehr note being edited
        // 2) it is the actively edited one and an async request is currently being made to the server
        notes.editing.active
          ? notes.editing.id === note.id && notes.editing.submitDisabled
          : false,
      footerDisabled,
      onChangeBody: body =>
        dispatch({
          type: SensorsActionTypes.SENSORS_NOTES_EDIT_NOTE_UPDATE,
          body,
        }),
    };

    switch (note.type) {
      case 'USER_CONFIGURATION':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteConfigAttachment
                capture={busiestCapture}
                config={note.data.config}
                configType={note.data.config_template}
                configStatus={note.data.config_status}
              />,
              <NoteDataGridAttachment
                items={[
                  {
                    id: 'capture-reannotation',
                    label: 'Captures sent for reannotation',
                    value: note.data.resent_captures_for_annotation ? 'Yes' : 'No',
                  },
                  {
                    id: 'config-version',
                    label: 'Config version',
                    value: typeof note.data.config['version'] === 'number' ? (
                      note.data.config['version']
                    ) : <Fragment>&mdash;</Fragment>,
                  },
                ]}
              />
            ]}
            {...noteProps}
          />
        );

      case 'USER_SENSOR_ADDED_TO_MPI':
      case 'USER_SENSOR_REMOVED_FROM_MPI':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                Sensor{' '}
                {note.type === 'USER_SENSOR_ADDED_TO_MPI'
                  ? 'added into '
                  : 'removed from '}
                <RichMPIName
                  id={note.data.mpi_id}
                  mpi={note.associated_metadata.multipoint_installation}
                />
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'USER_DATA_CAPTURES_SCHEDULED':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteDataGridAttachment
                items={[
                  {
                    id: 'time',
                    label: 'Runs At',
                    // NOTE: this is not a value that is UTC, this time is actually in
                    // "space local time". However, I couldn't come up with a
                    // less confusing way to display this than just using the raw
                    // value.
                    value: moment
                      .utc(note.data.time, 'hh:mm:ss')
                      .format('hh:mm:ss a'),
                  },
                  {
                    id: 'days_of_week',
                    label: 'Days Of Week',
                    value: (
                      <DayOfWeekPicker
                        daysOfWeek={note.data.days_of_week}
                        disabled
                      />
                    ),
                  },
                  {
                    id: 'end_date',
                    label: 'End Date',
                    value: moment(note.data.end_date).format('MM/DD/YYYY'),
                  },
                  {
                    id: 'num_captures',
                    label: 'Number Of Captures',
                    value: note.data.num_captures,
                  },
                  {
                    id: 'motion',
                    label: 'Motion',
                    value: note.data.num_event_retries
                      ? `Yes (${note.data.num_event_retries} event retries)`
                      : 'No',
                  },
                ]}
              />
            ]}
            {...noteProps}
          />
        );

      case 'USER_DATA_CAPTURES_TRIGGERED':
      case 'SYSTEM_AUTOMATIC_DATA_CAPTURES_TRIGGERED_SUCCESS':
        const triggerDataCapturesCommand = note.associated_metadata.command;
        return (
          <Note
            key={note.id}
            attachments={
              triggerDataCapturesCommand ? [
                <NoteDataGridAttachment
                  items={[
                    {
                      id: 'dense_capture',
                      label: 'Dense Capture',
                      value: triggerDataCapturesCommand.value.dense_capture
                        ? 'Yes'
                        : 'No',
                    },
                    triggerDataCapturesCommand.value.num_captures > 0 ? (
                      {
                        id: 'num_captures',
                        label: 'Number Of Captures',
                        value: triggerDataCapturesCommand.value.num_captures,
                      }
                    ) : (
                      {
                        id: 'capture_length',
                        label: 'Capture data for',
                        value: formatDuration(triggerDataCapturesCommand.value.max_capture_duration),
                      }
                    ),
                    {
                      id: 'duration',
                      label: 'Capture Duration',
                      value: formatDuration(
                        triggerDataCapturesCommand.value.duration,
                      ),
                    },
                    {
                      id: 'motion',
                      label: 'Motion',
                      value: triggerDataCapturesCommand.value.num_event_retries
                        ? `Yes (${triggerDataCapturesCommand.value.num_event_retries} event retries)`
                        : 'No',
                    },
                  ]}
                />
              ] : []
            }
            {...noteProps}
          />
        );

      case 'SYSTEM_ASSIGNED_TEAM_CHANGED':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                Team assignment changed from{' '}
                <strong>
                  {ASSIGNABLE_TEAM_TO_PRETTY_NAME[note.data.from_team] ||
                    'unknown'}
                </strong>{' '}
                to{' '}
                <strong>
                  {ASSIGNABLE_TEAM_TO_PRETTY_NAME[note.data.to_team] ||
                    'unknown'}
                </strong>
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_CURRENT_STATE_CHANGE':
        return (
          <Note
            key={note.id}
            collapsedTitleContext={note.data.after_state || 'NONE'}
            attachments={[
              <NoteTextAttachment>
                Sensor state changed from{' '}
                <strong>{note.data.before_state || 'NONE'}</strong> to{' '}
                <strong>{note.data.after_state || 'NONE'}</strong>{' '}
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_OS_UPDATE_REQUIRED':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                Current OS:{' '}
                <StatusTag status={note.data.current_build || 'NONE'} />
                <br></br>
                <br></br>
                <p>
                  This sensor is running an out of date operating system. To update a sensor's
                  operating system:
                </p>
                <ul>
                  <li>
                    Go to <a target="_blank" href={`${Hosts.CoreCucumberHost}/api/sensor/`}>core cucumber</a> and
                    click on <strong>{selectedSensor.serial_number}</strong>
                  </li>
                  <li>Select the latest sensor operating system and save the page</li>
                  <li>Wait for the sensor operating system to finish updating</li>
                  <li>Click "Remediate" below to put the sensor back into the system</li>
                </ul>
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'USER_PERFORMED_NOTE_ACTION':
        const noteActionName =
          note.associated_metadata.action_label || note.data.action_id;
        return (
          <Note
            key={note.id}
            collapsedTitleContext={noteActionName}
            attachments={[
              <NoteTextAttachment>
                Clicked the <strong>{noteActionName}</strong> action
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_STATUS_CHANGE':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                Sensor status changed from{' '}
                <StatusTag status={note.data.from_status} /> to{' '}
                <StatusTag status={note.data.to_status} />
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_EVENT_LOOP_OCCURRED_TOO_MANY_TIMES':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                <p>
                  This sensor has not reached 175 annotated events after 60 days - at the time of
                  this note's creation, it only had <strong>
                    {note.data.annotated_event_count} annotated events
                  </strong>
                  .
                </p>
                <p>
                  Something seems to be wrong with the sensor or its
                  environment.
                </p>
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_SLA_VALIDATED':
        return (
          <Note
            key={note.id}
            status="success"
            attachments={[
              <NoteTextAttachment>
                <strong>SLA Version</strong>: {note.data.sla_version}
                {note.associated_metadata.sla_version_description ? (
                  <Fragment>
                    <br />
                    <br />
                    <strong>SLA Description</strong>: {note.associated_metadata.sla_version_description}
                  </Fragment>
                ) : null}
                {note.associated_metadata.days_to_accuracy ? (
                  <Fragment>
                    <br />
                    <br />
                    <strong>Days to Accuracy</strong>: {Math.floor(note.associated_metadata.days_to_accuracy)} days
                  </Fragment>
                ) : null}
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_SLA_FAILED':
        return (
          <Note
            key={note.id}
            status="danger"
            attachments={[
              <NoteTextAttachment>
                <strong>SLA Version</strong>: {note.data.sla_version}
                {note.associated_metadata.sla_version_description ? (
                  <Fragment>
                    <br />
                    <br />
                    <strong>SLA Description</strong>: {note.associated_metadata.sla_version_description}
                  </Fragment>
                ) : null}
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_NOT_ONLINE':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                This sensor went offline in the middle of the automatic data{' '}
                capture process - its status is{' '}
                <StatusTag status={note.data.current_status} />. Sensor will
                retry a few attempts.
                <br></br>
                <br></br>
                Last Heartbeat: <StatusTag status={note.data.last_heartbeat} />
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_DATA_CAPTURE_COMMAND_FAILED':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                <p>
                  Start data capture command failed after multiple attempts.{' '}
                  {note.associated_metadata.sensor_current_status != 'online' ? (
                    <strong>It appears the sensor may be offline?</strong>
                  ) : null}
                </p>
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_UNABLE_TO_GENERATE_MORE_ANNOTATED_EVENTS':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                <p>
                  System did not get enough events to satisfy minimum
                  requirement of
                  <b> {note.data.annotated_event_increment_number} Events </b>
                  obtained since last event count. Sensor will retry a few
                  times.
                </p>
                <br></br>
                Last annotated event count:{' '}
                <b> {note.data.last_annotated_event_count}. </b>
                Current annotated event count:{' '}
                <b> {note.data.annotated_event_count}</b>
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_MPI_MANUAL_PROCESSING_REQUIRED':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                <p>
                  Accuracy SLA not yet supported for MPIs! This must be performed manually.
                </p>
              </NoteTextAttachment>
            ]}
            {...noteProps}
            onClickAction={noteAction => {
              if (noteAction.id === "SCHEDULE_CAPTURES") {
                // When clicked, open a client-side schedule captures modal
                const sensorsInMPI = note.associated_metadata.sensors_in_mpi as MultiPointInstallation['sensors'];
                const mpi = note.associated_metadata.mpi;
                setCaptureScheduleModalSensors(sensorsInMPI.map(sensor => ({
                  serial_number: sensor.serial_number,
                  link_id: sensor.link_id,
                  organization: {
                    id: mpi.organization_id,
                    name: mpi.organization_name,
                  },
                  sensor_type: sensor.sensor_type,
                  os_version: sensor.os_version,
                  doorway_name: sensor.doorway_name,
                })));
                setCaptureScheduleModalOpen(true);
                return
              }

              dispatch({
                type: SensorsActionTypes.SENSORS_NOTES_CLICK_NOTE_ACTION,
                noteId: note.id,
                noteAction,
              });
            }}
          />
        );

      case 'SYSTEM_SENSOR_READY_FOR_MANUAL_CONFIGURATION':
        return (
          <Note
            key={note.id}
            attachments={[
              (
                <NoteTextAttachment>
                  <p>
                    With its current configuration, this sensor is only{' '}
                    <strong>{note.data.total_accuracy}% accurate</strong> (under{' '}
                    {note.data.sla_accuracy_threshold}%). Please manually configure it to increase
                    its accuracy.
                  </p>
                </NoteTextAttachment>
              ),
              (
                <NoteDataGridAttachment
                  items={[
                    {
                      id: 'egress accuracy',
                      label: 'Egress Accuracy',
                      value:
                        note.data.egress_accuracy ? (
                          <ColoredPercent
                            percent={
                              note.data.egress_accuracy
                            }
                          />
                        ) : (
                          <Fragment>&mdash;</Fragment>
                        ),
                    },
                    {
                      id: 'annotated egresses',
                      label: 'Annotated Egresses',
                      tooltip:
                        'Egress events that are marked by an annotator; what we consider to be Ground Truth.',
                      value: note.data.egresses_annotated ? (
                        note.data.egresses_annotated
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    {
                      id: 'extra egresses',
                      label: 'Extra Egresses',
                      tooltip:
                        'Events where the algorithm marked an egress which does not have a corresponding egress annotation.',
                      value: note.data.egresses_extra ? (
                        note.data.egresses_extra
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    {
                      id: 'missed egresses',
                      label: 'Missed Egresses',
                      tooltip:
                        'Events where the algorithm did not register an egress, but where in actuality there is an egress annotation.',
                      value: note.data.egresses_missed ? (
                        note.data.egresses_missed
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    FORCE_BREAK,
                    {
                      id: 'ingress accuracy',
                      label: 'Ingress Accuracy',
                      value:
                        note.data.ingress_accuracy ? (
                          <ColoredPercent
                            percent={
                              note.data.ingress_accuracy
                            }
                          />
                        ) : (
                          <Fragment>&mdash;</Fragment>
                        ),
                    },
                    {
                      id: 'annotated ingresses',
                      label: 'Annotated Ingresses',
                      tooltip:
                        'Ingress events that are marked by an annotator; what we consider to be Ground Truth.',
                      value: note.data.ingresses_annotated ? (
                        note.data.ingresses_annotated
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    {
                      id: 'extra ingresses',
                      label: 'Extra Ingresses',
                      tooltip:
                        'Events where the algorithm marked an ingress which does not have a corresponding ingress annotation.',
                      value: note.data.ingresses_extra ? (
                        note.data.ingresses_extra
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    {
                      id: 'missed ingresses',
                      label: 'Missed Ingresses',
                      tooltip:
                        'Events where the algorithm did not register an ingress, but where in actuality there is an ingress annotation.',
                      value: note.data.ingresses_missed ? (
                        note.data.ingresses_missed
                      ) : (
                        <Fragment>&mdash;</Fragment>
                      ),
                    },
                    FORCE_BREAK,
                    {
                      id: 'overall accuracy',
                      label: 'Overall Accuracy',
                      value: (
                        <ColoredPercent
                          percent={
                            note.data.total_accuracy
                          }
                        />
                      ),
                    },
                  ]}
                />
              ),
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_HEIGHT_OUT_OF_SPECIFICATION':
        const inches = convertMetersToInchesForDisplay(note.data.measured_height_meters);
        const feetAndInches = convertMetersToFeetAndInchesForDisplay(note.data.measured_height_meters);
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                <p>
                  This sensor looks to be installed at a height of{' '}
                  <strong>{inches} inches</strong> (approximately {feetAndInches[0]}'{' '}
                  {feetAndInches[1]}" or {note.data.measured_height_meters} meters)
                  <br />
                  <br />
                  This is outside the height specification{' '}
                  ({convertMetersToInchesForDisplay(note.data.minimum_acceptable_height_meters)} inches -{' '}
                  {convertMetersToInchesForDisplay(note.data.maximum_acceptable_height_meters)} inches).
                </p>
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );
      case 'SYSTEM_SENSOR_HEIGHT_MEASUREMENT':
        const sensorInches = convertMetersToInchesForDisplay(note.data.measured_height_meters);
        const sensorFeetAndInches = convertMetersToFeetAndInchesForDisplay(note.data.measured_height_meters);
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                <p>
                  This sensor height measurement was{' '}
                  <strong>{sensorInches} inches</strong> (approximately {sensorFeetAndInches[0]}'{' '}
                  {sensorFeetAndInches[1]}" or {note.data.measured_height_meters} meters).{note.data.measured_height_stdev ? <span>{' '}The standard deviation of the measurement was {note.data.measured_height_stdev}</span> : null}
                </p>
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_BAD_DATA_QUALITY':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                <p>
                  It appears the protective film was left on this sensor by the installer, leading to low accuracy.
                  Please physically remove the film.
                </p>
                <br />
                <img src={note.associated_metadata.frame_png_signed_url} />
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_ACCURACY_LOOP_OCCURRED_TOO_MANY_TIMES':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteTextAttachment>
                <p>
                  This sensor went through the accuracy loop {note.data.loop_limit}{' '}
                  times, and was only able to reach{' '}
                  <strong>
                    {note.data.current_total_accuracy}% accuracy.
                  </strong>.
                  <br/>
                  <br/>
                  This sensor may require manual review.
                </p>
              </NoteTextAttachment>
            ]}
            {...noteProps}
          />
        );

      case 'SYSTEM_SENSOR_NEEDS_CONFIGURATION_VALIDATION':
        return (
          <Note
            key={note.id}
            attachments={[
              <NoteConfigAttachment
                capture={null}
                config={note.data.config}
                configType={note.data.config_template}
                configStatus={note.data.config_status}
              />,
            ]}
            {...noteProps}
          />
        );

      default:
        return <Note key={note.id} {...noteProps} />;
    }
  };

  switch (notes.resource.status) {
    case ResourceStatus.IDLE:
    case ResourceStatus.LOADING:
      return (
        <div className={styles.wrapper}>
          <Loading />
        </div>
      );

    case ResourceStatus.ERROR:
      return (
        <div className={styles.wrapper}>
          <ErrorState error={notes.resource.error} />
        </div>
      );

    case ResourceStatus.COMPLETE:
      const sortedNotes = notes.resource.data.sort(
        // Sort most recent timestamps first
        (a, b) => b.created_at.localeCompare(a.created_at),
      );

      // Sometimes, a bunch of notes will be created over and over by the system that clutters up the
      // note timeline. To make this easier to digest, group notes with the same type that have occurred
      // more than 5 times in a row.
      let renderableChunks = [];
      const numberOfLikeNotesToGroup = 5;
      for (let index = 0; index < sortedNotes.length; index += 1) {
        const note = sortedNotes[index];
        let indexWhereNoteTypeChanges =
          index + sortedNotes.slice(index).findIndex(n => n.type !== note.type);

        // If no note was found where the type changed, then all the rest of the notes must be the
        // same type.
        if (indexWhereNoteTypeChanges <= index-1) {
          indexWhereNoteTypeChanges = sortedNotes.length;
        }

        const numberOfLikeNotesInARow = indexWhereNoteTypeChanges - index - 1;

        const noteCreatedBySystem = note.user === null;

        if (
          !noteCreatedBySystem ||
          numberOfLikeNotesInARow < numberOfLikeNotesToGroup
        ) {
          renderableChunks.push({ chunkType: 'NOTE', note, chunkId: note.id });
          continue;
        }

        // At this point, there's definitely enough notes in a row to form a group.
        const startNote = sortedNotes[index];
        const chunkId = startNote.id;
        const endNote = sortedNotes[indexWhereNoteTypeChanges - 1];

        // Has the group been expanded by the user? If so, then just add all the notes individually
        // instead of the groups.
        if (expandedNoteGroupIds.includes(chunkId)) {
          const notesInGroup = sortedNotes.slice(
            index,
            indexWhereNoteTypeChanges - 1,
          );
          renderableChunks = [
            ...renderableChunks,
            ...notesInGroup.map(note => ({
              chunkType: 'NOTE',
              note,
              chunkId: note.id,
            })),
          ];
          index += numberOfLikeNotesInARow;
          continue;
        }

        renderableChunks.push({
          chunkType: 'NOTE_GROUP',
          chunkId,
          count: numberOfLikeNotesInARow,
          startNote,
          endNote,
        });
        index += numberOfLikeNotesInARow;
      }

      return (
        <div className={styles.notesWrapper} data-test-id="notes-wrapper">
          {notes.creation.active ? (
            <Note
              type="CREATION_FORM"
              from={{ email: '', full_name: '', picture: null }}
              footerDisabled
              body={null}
              attachments={[
                <AddUpdateNoteForm
                  width="100%"
                  placeholder="Leave a comment"
                  value={notes.creation.body}
                  onChangeValue={body =>
                    dispatch({
                      type: SensorsActionTypes.SENSORS_NOTES_CREATION_UPDATE,
                      body,
                    })
                  }
                  submitDisabled={notes.creation.submitDisabled}
                  buttons={
                    <Button
                      variant="underline"
                      onClick={() =>
                        dispatch({
                          type: SensorsActionTypes.SENSORS_NOTES_CREATION_CLOSE,
                        })
                      }
                    >
                      Cancel
                    </Button>
                  }
                  onSubmit={() =>
                    dispatch({
                      type: SensorsActionTypes.SENSORS_NOTES_CREATION_SUBMIT,
                      body: notes.creation.body,
                    })
                  }
                />
              ]}
            />
          ) : null}

          {/* Controlled by the "Schedule Captures" action on the mpi manual processing required
          note */}
          <CaptureScheduleModal
            open={captureScheduleModalOpen}
            onDismiss={() => setCaptureScheduleModalOpen(null)}
            sensors={captureScheduleModalSensors.map(sensor => ({
              serial_number: sensor.serial_number,
              link_id: sensor.link_id,
              name: sensor.doorway_name,
              organization: {
                id: sensor.organization.id,
                name: sensor.organization.name,
              },
              sensor_type: sensor.sensor_type,
            }))}
          />

          {renderableChunks.map(renderableChunk => {
            switch (renderableChunk.chunkType) {
              case 'NOTE':
                return renderNote(renderableChunk.note);
              case 'NOTE_GROUP':
                const noteType = NOTE_TYPES[renderableChunk.startNote.type]
                  ? NOTE_TYPES[renderableChunk.startNote.type].name
                  : renderableChunk.startNote.type;
                return (
                  <Fragment key={renderableChunk.chunkId}>
                    {renderNote(renderableChunk.startNote)}
                    <div
                      className={styles.hiddenNotesWrapper}
                      data-test-id="note-hidden"
                    >
                      <div className={styles.hiddenNotesInner}>
                        <HorizontalForm>
                          <span>
                            {renderableChunk.count - 2} hidden{' '}
                            <strong>{noteType}</strong> notes
                          </span>
                          <Button
                            onClick={() =>
                              setExpandedNoteGroupIds([
                                ...expandedNoteGroupIds,
                                renderableChunk.chunkId,
                              ])
                            }
                            data-test-id="note-hidden-expand"
                          >
                            Expand
                          </Button>
                        </HorizontalForm>
                      </div>
                    </div>
                    {renderNote(renderableChunk.endNote)}
                  </Fragment>
                );
              default:
                return null;
            }
          })}

          {!notes.creation.active && notes.resource.data.length === 0 ? (
            <div className={styles.notesEmptyState}>
              No notes found for this sensor. Would you like to&nbsp;
              <a
                tabIndex={0}
                onClick={() =>
                  dispatch({
                    type: SensorsActionTypes.SENSORS_NOTES_CREATION_OPEN,
                  })
                }
              >
                add a note
              </a>
              ?
            </div>
          ) : null}
        </div>
      );
  }
};
